/*
    The idea here is that we can manage the logging of errors at the redux level, I think this
    will make it easier, later on as we add more and more complexity to the app.
    In order to make the request meaningful I will need the users uid, the error (obviously), the current code in the 
    code editor, and a timestamp.

    Timestamp is handled on the backend
*/


import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { JSEncrypt } from 'jsencrypt';

const encryptor = new JSEncrypt();
const publicKey = process.env.REACT_APP_PUBLIC_KEY || '';
encryptor.setPublicKey(publicKey);

export interface ErrorLogPayload {
	error: string;
	code: string;
}

export interface LoggingState {
	status: 'idle' | 'loading' | 'succeeded' | 'failed';
	error: string | null;
	lastLoggedAt: string | null;
}

const initialState: LoggingState = {
	status: 'idle',
	error: null,
	lastLoggedAt: null,
};

export const logError = createAsyncThunk<unknown, ErrorLogPayload, { state: RootState; rejectValue: string }>(
  'loggingSlice/logError',
  async (payload, { getState, rejectWithValue }) => {
    const uid = getState().user.uid || 'unknown';

    if (!uid) {
      return rejectWithValue('No uid found in user slice');
    }

    const body = {
      user: String(uid),
      ...payload,
    };

    const headers = {
      'Content-Type': 'application/json', 
      'x-api-key': encryptor.encrypt(String(process.env.REACT_APP_LOG_API_KEY) || '' ),
    };

    try {
      const response = await axios.post('https://api.lbym.org/error-logs', body, {headers});
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          typeof error.response?.data === 'string'
            ? error.response.data
            : error.message || 'Failed to log error'
        );
      }
      return rejectWithValue('Failed to log error');
    }
  }
);

export const loggingSlice = createSlice({
	name: 'loggingSlice',
	initialState,
	reducers: {
		clearLoggingState: (state) => {
			state.status = 'idle';
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(logError.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(logError.fulfilled, (state) => {
				state.status = 'succeeded';
				state.lastLoggedAt = new Date().toISOString();
			})
			.addCase(logError.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload ? String(action.payload) : action.error.message ?? 'Failed to log error';
			});
	},
});

export const { clearLoggingState } = loggingSlice.actions;
export const selectLoggingStatus = (state: RootState) => state.logging.status;
export const selectLoggingError = (state: RootState) => state.logging.error;
export const selectLastLoggedAt = (state: RootState) => state.logging.lastLoggedAt;


export default loggingSlice.reducer;

